// This module handles cart totals, discount calculations, and cart UI updates

(function() {
  'use strict';

  window.PB_calculateTotal = function(state, config) {
    const allSelected = state.selections.flatMap(s =>
      (s.selectedProducts || []).map(p => ({
        ...p,
        className: p.product_type || s.productClassName,
        classId: s.productClassId
      }))
    );

    // Count unique classes using classId (excluding certain categories)
    // Special: refrigerators and column-refrigerators count as 1 category
    const eligibleClasses = new Set(
      allSelected
        .filter(p => !config.excludedFromDiscount.some(exc => p.className.includes(exc)))
        .map(p => {
          // Treat refrigerators and column-refrigerators as single "refrigeration" category
          if (p.classId === 'refrigerators' || p.classId === 'column-refrigerators') {
            return 'refrigeration-family';
          }
          return p.classId;
        })
    );

    const pieceCount = eligibleClasses.size;

    // Determine discount
    let discountPercent = 0;
    if (pieceCount === 2) discountPercent = 0.05;
    else if (pieceCount === 3) discountPercent = 0.07;
    else if (pieceCount >= 4 && pieceCount <= 5) discountPercent = 0.1;
    else if (pieceCount >= 6) discountPercent = 0.12;

    // Group products by category using classId
    const productsByCategory = {};
    allSelected.forEach(product => {
      if (!productsByCategory[product.classId]) {
        productsByCategory[product.classId] = [];
      }
      productsByCategory[product.classId].push(product);
    });

    // Count refrigeration items
    const fullSizeProducts = productsByCategory['refrigerators'] || [];
    const columnProducts = productsByCategory['column-refrigerators'] || [];
    const fullSizeCount = fullSizeProducts.length;
    const columnCount = columnProducts.length;

    // Identify products that should receive discount
    const discountEligibleProductIds = new Set();

    Object.keys(productsByCategory).forEach(classId => {
      const products = productsByCategory[classId];

      // Special handling for refrigeration categories
      if (classId === 'refrigerators' || classId === 'column-refrigerators') {
        // Skip here - handle refrigeration separately below
        return;
      }

      // Standard logic for non-refrigeration: highest priced per category
      const sortedByPrice = products.sort((a, b) => {
        const priceA = parseFloat(a.variants[0].price);
        const priceB = parseFloat(b.variants[0].price);
        return priceB - priceA;
      });
      if (sortedByPrice.length > 0) {
        discountEligibleProductIds.add(sortedByPrice[0].id);
      }
    });

    // Special refrigeration discount logic
    if (fullSizeCount === 1 && columnCount === 1) {
      // Exactly 1 of each: highest-priced wins
      const fullSizePrice = parseFloat(fullSizeProducts[0].variants[0].price);
      const columnPrice = parseFloat(columnProducts[0].variants[0].price);
      if (fullSizePrice >= columnPrice) {
        discountEligibleProductIds.add(fullSizeProducts[0].id);
      } else {
        discountEligibleProductIds.add(columnProducts[0].id);
      }
    } else if (columnCount > 0) {
      // Any other combo with columns: all columns get discount, full-size gets nothing
      columnProducts.forEach(p => discountEligibleProductIds.add(p.id));
    } else if (fullSizeCount > 0) {
      // Only full-size (no columns): highest full-size gets discount
      const sortedFullSize = fullSizeProducts.sort((a, b) => {
        const priceA = parseFloat(a.variants[0].price);
        const priceB = parseFloat(b.variants[0].price);
        return priceB - priceA;
      });
      discountEligibleProductIds.add(sortedFullSize[0].id);
    }

    let subtotal = 0;
    let yourPriceSubtotal = 0;
    let bundleSavings = 0;
    let discountAmount = 0;
    let afterBundleDiscountTotal = 0;
    let finalTotal = 0;

    allSelected.forEach(product => {
      const variant = product.variants[0];
      const qty = window.PB_getQuantity ? window.PB_getQuantity(product.id) : 1;
      let price = parseFloat(variant.price);
      let mapPrice = price;
      let pmapPrice = null;
      let yourPrice = price;
      let marginPercent = 0;

      if (window.PB_MAP_PRICES && window.PB_MAP_PRICES[product.id]) {
        const priceData = window.PB_MAP_PRICES[product.id];
        if (priceData.map) mapPrice = parseFloat(priceData.map);
        if (priceData.pmap) pmapPrice = parseFloat(priceData.pmap);
        if (priceData.yourPrice) yourPrice = parseFloat(priceData.yourPrice);
        if (priceData.marginPercent) marginPercent = parseFloat(priceData.marginPercent);
      }

      const hasPromoTag = product.tags?.some(tag => tag.toLowerCase() === 'promo');
      const hasPromo2Tag = product.tags?.some(tag => tag.toLowerCase() === 'promo 2');
      const isExcludedFromCategory = config.excludedFromDiscount.some(exc => product.className.includes(exc));

      let displayPrice;
      let productBundleDiscount = 0;
      let priceAfterBundleDiscount;
      let productFinalPrice;

      if (hasPromo2Tag) {
        // Promo 2: Use helper - PMAP, no bundle discount — all units at same price
        const promo2Pricing = window.PB_getPromo2Pricing({
          pmapPrice,
          mapPrice,
          variantPrice: parseFloat(variant.price),
          marginPercent,
          bundleDiscountPercent: isExcludedFromCategory ? 0 : discountPercent,
          isLoggedIn: window.PB_CUSTOMER_LOGGED_IN
        });
        displayPrice = promo2Pricing.displayPrice;
        productBundleDiscount = 0;
        priceAfterBundleDiscount = displayPrice * qty;
        productFinalPrice = promo2Pricing.yourPrice * qty;
      } else if (hasPromoTag) {
        // Promo: PMAP at < 6 items, MAP at 6+ items with 12% discount
        const promoPricing = window.PB_getPromoPricing({
          pmapPrice,
          mapPrice,
          variantPrice: parseFloat(variant.price),
          marginPercent,
          bundleDiscountPercent: isExcludedFromCategory ? 0 : discountPercent,
          isLoggedIn: window.PB_CUSTOMER_LOGGED_IN
        });
        displayPrice = promoPricing.displayPrice;

        if (discountEligibleProductIds.has(product.id) && promoPricing.productDiscount > 0 && !isExcludedFromCategory) {
          // 1 unit gets the promo discount, extra units at PMAP (no discount)
          productBundleDiscount = promoPricing.productDiscount;
          const extraUnits = qty - 1;
          if (extraUnits > 0) {
            const extraPricing = window.PB_getPromoPricing({
              pmapPrice,
              mapPrice,
              variantPrice: parseFloat(variant.price),
              marginPercent,
              bundleDiscountPercent: 0,
              isLoggedIn: window.PB_CUSTOMER_LOGGED_IN
            });
            priceAfterBundleDiscount = promoPricing.priceAfterDiscount + (extraPricing.displayPrice * extraUnits);
            productFinalPrice = promoPricing.yourPrice + (extraPricing.yourPrice * extraUnits);
          } else {
            priceAfterBundleDiscount = promoPricing.priceAfterDiscount;
            productFinalPrice = promoPricing.yourPrice;
          }
        } else {
          // Not eligible or no discount — all units at same price
          productBundleDiscount = 0;
          priceAfterBundleDiscount = promoPricing.priceAfterDiscount * qty;
          productFinalPrice = promoPricing.yourPrice * qty;
        }
      } else {
        // Regular items
        displayPrice = pmapPrice || mapPrice || price;

        if (discountEligibleProductIds.has(product.id) && discountPercent > 0 && !isExcludedFromCategory) {
          productBundleDiscount = parseFloat((displayPrice * discountPercent).toFixed(2));
        }

        const discountedUnitPrice = parseFloat((displayPrice - productBundleDiscount).toFixed(2));
        const extraUnits = productBundleDiscount > 0 ? qty - 1 : qty;
        const discountedUnits = productBundleDiscount > 0 ? 1 : 0;

        priceAfterBundleDiscount = (discountedUnitPrice * discountedUnits) + (displayPrice * extraUnits);

        if (window.PB_CUSTOMER_LOGGED_IN && marginPercent > 0) {
          const marginMultiplier = (1 - marginPercent / 100);
          productFinalPrice = (discountedUnitPrice * marginMultiplier * discountedUnits) + (displayPrice * marginMultiplier * extraUnits);
        } else {
          productFinalPrice = priceAfterBundleDiscount;
        }
      }

      // Use MAP as the reference ("was") price so total savings capture
      // both bundle discount AND PMAP savings (MAP - PMAP) on units at PMAP.
      subtotal += mapPrice * qty;
      yourPriceSubtotal += yourPrice * qty;
      bundleSavings += productBundleDiscount;
      afterBundleDiscountTotal += priceAfterBundleDiscount;
      finalTotal += productFinalPrice;
    });

    // Total savings = MAP subtotal − after-bundle-discount total.
    // Split: bundleSavings is accumulated above; pmapSavings is the remainder.
    discountAmount = Math.max(0, parseFloat((subtotal - afterBundleDiscountTotal).toFixed(2)));
    bundleSavings = parseFloat(bundleSavings.toFixed(2));
    const pmapSavings = Math.max(0, parseFloat((discountAmount - bundleSavings).toFixed(2)));

    return {
      subtotal,
      yourPriceSubtotal,
      discountAmount,
      bundleSavings,
      pmapSavings,
      afterBundleDiscountTotal,
      total: finalTotal,
      bundleDiscountPercent: discountPercent,
      discountEligibleProductIds
    };
  };

  window.PB_updateCart = function(state, config) {
    const allSelected = state.selections.flatMap(s =>
      (s.selectedProducts || []).map(p => ({
        ...p,
        className: p.product_type || s.productClassName,
        classId: s.productClassId
      }))
    );

    const { subtotal, discountAmount, bundleSavings, pmapSavings, afterBundleDiscountTotal, total, bundleDiscountPercent, discountEligibleProductIds } = window.PB_calculateTotal(state, config);

    // Store current totals for print to use (so print uses page values, not recalculated)
    window.PB_CURRENT_TOTALS = {
      subtotal,
      discountAmount,
      bundleSavings,
      pmapSavings,
      afterBundleDiscountTotal,
      total,
      bundleDiscountPercent,
      discountEligibleProductIds
    };

    const formatCurrency = (amount) => {
      const truncated = Math.floor(amount * 100) / 100;
      return '$' + truncated.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };

    const itemCount = allSelected.reduce((sum, p) => sum + (window.PB_getQuantity ? window.PB_getQuantity(p.id) : 1), 0);
    document.getElementById('pbItemCount').textContent = itemCount;
    document.getElementById('pbTotal').textContent = formatCurrency(afterBundleDiscountTotal);

    const badge = document.getElementById('pbSummaryBadge');
    if (badge) badge.textContent = itemCount;

    // Toggle sections visibility
    const heroSection = document.getElementById('pbHeroSection');
    const unifiedHeader = document.querySelector('.pb-unified-header');
    const leftSection = document.querySelector('.pb-left-section');
    const rightSection = document.querySelector('.pb-right-section');

    if (itemCount > 0) {
      if (heroSection) heroSection.classList.add('hidden');
      if (unifiedHeader) unifiedHeader.classList.add('visible');
      if (leftSection) {
        leftSection.classList.add('visible', 'with-cart');
        leftSection.classList.remove('full-width');
      }
      if (rightSection) rightSection.classList.add('visible');
    } else {
      const hasStartedBuilding = leftSection && leftSection.classList.contains('visible');

      if (!hasStartedBuilding) {
        if (heroSection) heroSection.classList.remove('hidden');
        if (unifiedHeader) unifiedHeader.classList.remove('visible');
        if (leftSection) leftSection.classList.remove('visible', 'with-cart');
      } else {
        if (heroSection) heroSection.classList.add('hidden');
        if (unifiedHeader) unifiedHeader.classList.add('visible');
        if (leftSection) {
          leftSection.classList.add('visible', 'full-width');
          leftSection.classList.remove('with-cart');
        }
      }
      if (rightSection) rightSection.classList.remove('visible');
    }

    // Render product list
    const productsContainer = document.getElementById('pbSummaryProducts');
    if (allSelected.length === 0) {
      productsContainer.innerHTML = '';
    } else {
      productsContainer.innerHTML = allSelected.map(product =>
        renderCartProduct(product, bundleDiscountPercent, discountEligibleProductIds, config, formatCurrency)
      ).join('');
    }

    // Update MSRP display
    const msrpElement = document.getElementById('pbMSRP');
    msrpElement.textContent = formatCurrency(subtotal);
    if (discountAmount > 0 && itemCount > 1) {
      msrpElement.classList.add('has-discount');
    } else {
      msrpElement.classList.remove('has-discount');
    }

    // Your Price Total for logged-in customers
    const yourPriceRow = document.getElementById('pbYourPriceRow');
    if (window.PB_CUSTOMER_LOGGED_IN && allSelected.length > 0) {
      yourPriceRow.style.display = '';
      document.getElementById('pbYourPriceTotal').textContent = formatCurrency(total);
    } else {
      yourPriceRow.style.display = 'none';
    }

    // Update savings display: show total savings with bundle + PMAP split
    const savingsEl = document.getElementById('pbSavings');
    if (discountAmount > 0 && allSelected.length > 0) {
      let breakdown = '';
      if (bundleSavings > 0 && pmapSavings > 0) {
        breakdown = ` (${formatCurrency(bundleSavings)} bundle + ${formatCurrency(pmapSavings)} PMAP)`;
      } else if (bundleSavings > 0) {
        breakdown = ' (bundle)';
      } else if (pmapSavings > 0) {
        breakdown = ' (PMAP)';
      }
      savingsEl.textContent = `-${formatCurrency(discountAmount)}${breakdown}`;
    } else {
      savingsEl.textContent = '-$0';
    }
  };

  /**
   * Render a product in the cart
   */
  function renderCartProduct(product, bundleDiscountPercent, discountEligibleProductIds, config, formatCurrency) {
    const variant = product.variants[0];
    const sku = variant.sku || 'N/A';

    let mapPrice = null;
    let pmapPrice = null;
    let displayPrice = parseFloat(variant.price);
    let productMarginPercent = 0;

    if (window.PB_MAP_PRICES && window.PB_MAP_PRICES[product.id]) {
      const priceData = window.PB_MAP_PRICES[product.id];
      if (priceData.map) mapPrice = parseFloat(priceData.map);
      if (priceData.pmap) pmapPrice = parseFloat(priceData.pmap);
      if (priceData.marginPercent) productMarginPercent = parseFloat(priceData.marginPercent);
    }

    const hasPromoTag = product.tags?.some(tag => tag.toLowerCase() === 'promo');
    const hasPromo2Tag = product.tags?.some(tag => tag.toLowerCase() === 'promo 2');
    const isExcludedFromCategory = config.excludedFromDiscount.some(exc => product.className.includes(exc));

    let productDiscount = 0;
    let promo2Pricing = null;
    let promoPricing = null;

    if (hasPromo2Tag) {
      promo2Pricing = window.PB_getPromo2Pricing({
        pmapPrice,
        mapPrice,
        variantPrice: parseFloat(variant.price),
        marginPercent: productMarginPercent,
        bundleDiscountPercent,
        isLoggedIn: window.PB_CUSTOMER_LOGGED_IN
      });
      displayPrice = promo2Pricing.displayPrice;
      productDiscount = isExcludedFromCategory ? 0 : promo2Pricing.productDiscount;
    } else if (hasPromoTag) {
      promoPricing = window.PB_getPromoPricing({
        pmapPrice,
        mapPrice,
        variantPrice: parseFloat(variant.price),
        marginPercent: productMarginPercent,
        bundleDiscountPercent: isExcludedFromCategory ? 0 : bundleDiscountPercent,
        isLoggedIn: window.PB_CUSTOMER_LOGGED_IN
      });
      displayPrice = promoPricing.displayPrice;
      productDiscount = promoPricing.productDiscount;
    } else {
      displayPrice = pmapPrice || mapPrice || displayPrice;
      if (discountEligibleProductIds.has(product.id) && bundleDiscountPercent > 0) {
        if (!isExcludedFromCategory) {
          productDiscount = parseFloat((displayPrice * bundleDiscountPercent).toFixed(2));
        }
      }
    }

    const productUrl = product.handle ? `/products/${product.handle}` : '#';

    const qty = window.PB_getQuantity ? window.PB_getQuantity(product.id) : 1;

    // Build pricing HTML
    const pricingHTML = buildPricingHTML(product, variant, hasPromoTag, hasPromo2Tag, promo2Pricing, promoPricing,
      pmapPrice, mapPrice, displayPrice, productDiscount, productMarginPercent, bundleDiscountPercent,
      discountEligibleProductIds, isExcludedFromCategory, formatCurrency, qty);

    const imageUrl = product.images?.[0]?.src || '';

    return `
      <div class="pb-product-item">
        ${imageUrl ? `<div class="pb-cart-product-image"><a href="${productUrl}" target="_blank"><img src="${imageUrl}" alt="${product.title}" loading="lazy"></a></div>` : ''}
        <div class="pb-product-info">
          <a href="${productUrl}" target="_blank" class="pb-product-name">
            ${product.title}
          </a>
          <div class="pb-product-sku">
            SKU: ${sku} | ${product.classId === 'under-counter-refrigeration' ? 'Under Counter Refrigeration' : (product.classId === 'column-refrigerators' ? 'Column Refrigeration' : product.className)}${product.selectedFinish ? ' | ' + product.selectedFinish : ''}${product.selectedAccent ? ' | ' + product.selectedAccent : ''}
          </div>
          <div class="pb-qty-row">
            <span class="pb-qty-label">Qty:</span>
            <div class="pb-qty-controls">
              <button class="pb-qty-minus" data-class-id="${product.classId}" data-product-id="${product.id}" title="Decrease quantity">
                <i class="fas fa-minus"></i>
              </button>
              <span class="pb-qty-value">${qty}</span>
              <button class="pb-qty-plus" data-class-id="${product.classId}" data-product-id="${product.id}" title="Increase quantity">
                <i class="fas fa-plus"></i>
              </button>
            </div>
          </div>
        </div>
        <div class="pb-product-pricing">
          ${pricingHTML}
        </div>
         <button class="pb-product-delete" data-class-id="${product.classId}" data-product-id="${product.id}" title="Remove product">
          <i class="fas fa-trash-alt"></i>
        </button>
      </div>
    `;
  }

  /**
   * Build pricing display HTML for cart product
   */
  function buildPricingHTML(product, variant, hasPromoTag, hasPromo2Tag, promo2Pricing, promoPricing,
    pmapPrice, mapPrice, displayPrice, productDiscount, productMarginPercent, bundleDiscountPercent,
    discountEligibleProductIds, isExcludedFromCategory, formatCurrency, qty) {

    qty = qty || 1;
    let priceHTML = '';

    // Quantity annotation helper — shown after per-unit pricing when qty > 1
    function qtyLineTotal(unitPrice) {
      if (qty <= 1) return '';
      return `<div style="font-size: 0.8rem; color: #888; margin-top: 4px;">Line total (x${qty}): ${formatCurrency(unitPrice * qty)}</div>`;
    }
    function qtyDiscountNote() {
      if (qty <= 1 || productDiscount <= 0) return '';
      return `<div style="font-size: 0.75rem; color: #888; font-style: italic; margin-top: 2px;">Discount applied to 1 unit, ${qty - 1} at full price</div>`;
    }

    // Check if this is a plumbing product - show simplified pricing without discount text
    const isPlumbingProduct = product.classId === 'plumbing';
    if (isPlumbingProduct) {
      const priceLabel = pmapPrice ? 'PMAP' : (mapPrice ? 'MAP' : 'Price');
      priceHTML += `<div style="font-size: 0.9rem; color: #666; margin-bottom: 4px;">${priceLabel}: ${formatCurrency(displayPrice)}</div>`;

      if (window.PB_CUSTOMER_LOGGED_IN) {
        const dealerPrice = parseFloat(variant.price);
        let yourPrice = dealerPrice;
        if (window.PB_MAP_PRICES && window.PB_MAP_PRICES[product.id] && window.PB_MAP_PRICES[product.id].yourPrice) {
          yourPrice = parseFloat(window.PB_MAP_PRICES[product.id].yourPrice);
        }
        priceHTML += `<div style="font-size: 1.1rem; font-weight: 700; color: #16a34a; margin-top: 4px; padding-top: 4px; border-top: 1px solid #e5e7eb;">Final Price: ${formatCurrency(yourPrice)}</div>`;
        if (productMarginPercent > 0) {
          priceHTML += `<div style="font-size: 0.75rem; color: #666; font-style: italic; margin-top: 2px;">(${productMarginPercent.toFixed(2)}% margin)</div>`;
        }
        priceHTML += qtyLineTotal(yourPrice);
      } else {
        priceHTML += qtyLineTotal(displayPrice);
      }
      return priceHTML;
    }

    // Handle Promo 2 items
    if (hasPromo2Tag && promo2Pricing) {
      priceHTML += `<div style="font-size: 0.9rem; color: #666; margin-bottom: 4px;">${promo2Pricing.priceLabel}: ${formatCurrency(promo2Pricing.displayPrice)}</div>`;

      if (window.PB_CUSTOMER_LOGGED_IN) {
        priceHTML += `<div style="font-size: 1.1rem; font-weight: 700; color: #16a34a; margin-top: 4px; padding-top: 4px; border-top: 1px solid #e5e7eb;">Final Price: ${formatCurrency(promo2Pricing.yourPrice)}</div>`;
        if (productMarginPercent > 0) {
          priceHTML += `<div style="font-size: 0.75rem; color: #666; font-style: italic; margin-top: 2px;">(${productMarginPercent.toFixed(2)}% margin)</div>`;
        }
        priceHTML += qtyLineTotal(promo2Pricing.yourPrice);
      } else {
        priceHTML += qtyLineTotal(promo2Pricing.displayPrice);
      }

      if (promo2Pricing.eligibilityMessage) {
        priceHTML += `<div class="pb-product-excluded" style="margin-top: 4px;">${promo2Pricing.eligibilityMessage}</div>`;
      }
      return priceHTML;
    }

    // Handle Promo items
    if (hasPromoTag && promoPricing) {
      if (promoPricing.showStrikethroughPmap) {
        priceHTML += `<div style="font-size: 0.9rem; color: #999; margin-bottom: 2px; text-decoration: line-through;">PMAP: ${formatCurrency(promoPricing.originalPmapPrice)}</div>`;
        priceHTML += `<div style="font-size: 0.9rem; color: #666; margin-bottom: 4px; font-weight: 600;">MAP: ${formatCurrency(promoPricing.displayPrice)}</div>`;
      } else {
        priceHTML += `<div style="font-size: 0.9rem; color: #666; margin-bottom: 4px;">${promoPricing.priceLabel}: ${formatCurrency(promoPricing.displayPrice)}</div>`;
      }

      if (promoPricing.productDiscount > 0) {
        priceHTML += `<div style="font-size: 0.9rem; color: #dc2626; font-weight: 600; margin-bottom: 4px;">Discount: -${formatCurrency(promoPricing.productDiscount)} (12% off MAP)</div>`;
        priceHTML += qtyDiscountNote();
        if (window.PB_CUSTOMER_LOGGED_IN) {
          priceHTML += `<div style="font-size: 0.9rem; color: #333; font-weight: 600; margin-bottom: 4px;">After Bundle Discount: ${formatCurrency(promoPricing.priceAfterDiscount)}</div>`;
        }
      }

      if (window.PB_CUSTOMER_LOGGED_IN) {
        priceHTML += `<div style="font-size: 1.1rem; font-weight: 700; color: #16a34a; margin-top: 4px; padding-top: 4px; border-top: 1px solid #e5e7eb;">Final Price: ${formatCurrency(promoPricing.yourPrice)}</div>`;
        if (productMarginPercent > 0) {
          priceHTML += `<div style="font-size: 0.75rem; color: #666; font-style: italic; margin-top: 2px;">(${productMarginPercent.toFixed(2)}% margin)</div>`;
        }
        priceHTML += qtyLineTotal(promoPricing.yourPrice);
      } else if (promoPricing.productDiscount > 0) {
        priceHTML += `<div style="font-size: 1.1rem; font-weight: 700; color: #16a34a; margin-top: 4px; padding-top: 4px; border-top: 1px solid #e5e7eb;">After Discount: ${formatCurrency(promoPricing.priceAfterDiscount)}</div>`;
        priceHTML += qtyLineTotal(promoPricing.priceAfterDiscount);
      } else {
        priceHTML += qtyLineTotal(promoPricing.displayPrice);
      }

      if (promoPricing.eligibilityMessage) {
        priceHTML += `<div class="pb-product-excluded" style="margin-top: 4px;">${promoPricing.eligibilityMessage}</div>`;
      }
      return priceHTML;
    }

    // Regular items
    const dealerPrice = parseFloat(variant.price);
    let yourPriceFromData = dealerPrice;
    if (window.PB_MAP_PRICES && window.PB_MAP_PRICES[product.id]) {
      const priceData = window.PB_MAP_PRICES[product.id];
      if (priceData.yourPrice) yourPriceFromData = parseFloat(priceData.yourPrice);
    }

    let calculatedYourPrice;
    if (window.PB_CUSTOMER_LOGGED_IN && productMarginPercent > 0) {
      const priceAfterBundleDiscount = displayPrice - productDiscount;
      calculatedYourPrice = priceAfterBundleDiscount * (1 - productMarginPercent / 100);
    } else {
      calculatedYourPrice = yourPriceFromData;
    }

    const priceLabel = pmapPrice ? 'PMAP' : (mapPrice ? 'MAP' : 'Price');
    priceHTML += `<div style="font-size: 0.9rem; color: #666; margin-bottom: 4px;">${priceLabel}: ${formatCurrency(displayPrice)}</div>`;

    if (productDiscount > 0) {
      let discountLabel = `(${(bundleDiscountPercent * 100).toFixed(0)}% Bundle Discount)`;
      priceHTML += `<div style="font-size: 0.9rem; color: #dc2626; font-weight: 600; margin-bottom: 4px;">Discount: -${formatCurrency(productDiscount)} ${discountLabel}</div>`;
      priceHTML += qtyDiscountNote();

      const priceAfterBundleDiscount = displayPrice - productDiscount;

      if (window.PB_CUSTOMER_LOGGED_IN) {
        priceHTML += `<div style="font-size: 0.9rem; color: #333; font-weight: 600; margin-bottom: 4px;">After Bundle Discount: ${formatCurrency(priceAfterBundleDiscount)}</div>`;
      }
    }

    if (window.PB_CUSTOMER_LOGGED_IN) {
      priceHTML += `<div style="font-size: 1.1rem; font-weight: 700; color: #16a34a; margin-top: 4px; padding-top: 4px; border-top: 1px solid #e5e7eb;">Final Price: ${formatCurrency(calculatedYourPrice)}</div>`;
      if (productMarginPercent > 0) {
        priceHTML += `<div style="font-size: 0.75rem; color: #666; font-style: italic; margin-top: 2px;">(${productMarginPercent.toFixed(2)}% margin)</div>`;
      }
      if (qty > 1) {
        // Line total: 1 discounted unit + (qty-1) full price units
        const fullUnitFinalPrice = window.PB_CUSTOMER_LOGGED_IN && productMarginPercent > 0
          ? displayPrice * (1 - productMarginPercent / 100) : displayPrice;
        const lineTotal = calculatedYourPrice + fullUnitFinalPrice * (qty - 1);
        priceHTML += `<div style="font-size: 0.8rem; color: #888; margin-top: 4px;">Line total (x${qty}): ${formatCurrency(lineTotal)}</div>`;
      }
    } else if (productDiscount > 0) {
      const priceAfterDiscount = displayPrice - productDiscount;
      priceHTML += `<div style="font-size: 1.1rem; font-weight: 700; color: #16a34a; margin-top: 4px; padding-top: 4px; border-top: 1px solid #e5e7eb;">After Discount: ${formatCurrency(priceAfterDiscount)}</div>`;
      if (qty > 1) {
        const lineTotal = priceAfterDiscount + displayPrice * (qty - 1);
        priceHTML += `<div style="font-size: 0.8rem; color: #888; margin-top: 4px;">Line total (x${qty}): ${formatCurrency(lineTotal)}</div>`;
      }
    } else if (qty > 1) {
      priceHTML += `<div style="font-size: 0.8rem; color: #888; margin-top: 4px;">Line total (x${qty}): ${formatCurrency(displayPrice * qty)}</div>`;
    }

    if (isExcludedFromCategory && bundleDiscountPercent > 0) {
      priceHTML += '<div class="pb-product-excluded" style="margin-top: 4px;">Excluded from bundle discount</div>';
    }

    return priceHTML;
  }

  console.log('Package Builder Cart loaded');
})();
